// Results column: "Calcolo non eseguito" banner, the two groups of KPI cards and the "Viti fuori inviluppo" card, all
// DS2 Cards. A KPI card shows per row the label, the big value (unit in a <small>, green / red for an ok / bad check)
// and the formula. When the calculation is blocked the KPI cards are hidden (HTML .results-col.calc-blocked).
import cx from "classnames";
import { memo, useMemo, type FC } from "react";
import { Card } from "../../../components/ds2/Card";
import { MessageBox } from "../../../components/ds2/MessageBox";
import useI18n from "../../../hooks/useTranslations";
import type { CalcState, ViolationItem } from "../../../http/screwConnector/formulas";
import Html from "./Html";
import styles from "./ResultsColumn.module.css";
import { buildResultCards, placeholderResultCards } from "./resultCards";

type ResultsColumnProps = {
  state: CalcState | null;
  blockReason: string | null;
  // ui.violations: [] = all screws admissible, null = card hidden
  violations: ViolationItem[] | null;
};

const ResultsColumn: FC<ResultsColumnProps> = ({ state, blockReason, violations }) => {
  const t = useI18n().translation.screwConnectorModule.calculation;
  const grids = useMemo(
    () => (state && !blockReason ? buildResultCards(state, t.stats, t) : placeholderResultCards(t.stats)),
    [state, blockReason, t],
  );

  return (
    <>
      {blockReason ? (
        <MessageBox type="error">
          {t.violations.blockedPrefix}
          {blockReason}
        </MessageBox>
      ) : (
        grids.map((grid, i) => (
          <div key={i} className={styles.grid}>
            {grid.map((card) => (
              <Card key={card.key} data-stat={card.key}>
                {card.rows.map((row, j) => (
                  <div key={j} className={styles.row}>
                    <Html text={row.labelHtml} />
                    <Html text={row.valueHtml} className={cx(styles.value, card.tone && styles[card.tone])} />
                    {row.formulaHtml ? <Html text={row.formulaHtml} className={styles.formula} /> : null}
                  </div>
                ))}
              </Card>
            ))}
          </div>
        ))
      )}
      {violations && !blockReason ? (
        <Card>
          <h3 className={styles.violationsTitle}>{t.violations.title}</h3>
          {violations.length === 0 ? (
            <MessageBox type="success">{t.violations.allOk}</MessageBox>
          ) : (
            <MessageBox type="error">
              <div className={styles.violList}>
                {violations.map((p, i) => (
                  <div key={i} className={styles.violItem}>
                    <span className={styles.violPoint}>
                      ({p.x.toFixed(0)}, {p.y.toFixed(0)})
                    </span>
                    {p.violations.join("; ")}
                  </div>
                ))}
              </div>
            </MessageBox>
          )}
        </Card>
      ) : null}
    </>
  );
};

// Memoized: it re-renders through its own props (the engine view among them) and contexts, not whenever its parent
// does.
export default memo(ResultsColumn);
